import React, { useState } from 'react';
import { Button, Checkbox, Input } from 'antd';
import styled from 'styled-components';

import { handleFormErrors } from '../../common/errorHelpers';
import { DictionaryType } from '../../common/types';
import { StyledButton } from '../../components/Button';
import { StyledRow } from '../../components/StyledRow';
import Icon, { ButtonIcon, iconColors } from '../../icons/Icon';

import { DictionaryItemsListContainer, InputListItem, ListLabel, StyledListItem } from './itemsList.styles';
import { useDeleteDictionaryItem } from './useDeleteDictionaryItem';
import { useGetDictionaryByName } from './useGetDictionaryByName';
import { useSaveDictionaryItem } from './useSaveDictionaryItem';
import { useUpdateDictionaryItem } from './useUpdateDictionaryItem';

export const StyledEditButton = styled(Button)`
  color: #bdbdbd;
  :hover {
    color: black !important;
    background-color: #f5f5f5 !important;
  }
`;

export const StyledDeleteButton = styled(Button)`
  color: #bdbdbd;
  :hover {
    color: #de3a3a !important;
    background-color: #f5f5f5 !important;
  }
`;

export const AddButtonIcon = styled(ButtonIcon).attrs({
  name: 'Plus',
  style: { color: `${iconColors.white}` },
})``;

export const SaveButtonIcon = styled(ButtonIcon).attrs({
  name: 'Save',
  style: { color: `${iconColors.white}` },
})``;

type ListItemProps = {
  item: DictionaryType;
  isChoosable?: boolean;
  selected: boolean;
  onClick: (item: DictionaryType) => void;
  onSave: (item: DictionaryType) => void;
  onDelete: (item: DictionaryType) => void;
};

const ListItem: React.FC<ListItemProps> = ({ item, isChoosable = false, selected, onDelete, onSave, onClick }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(item.value);

  const handleSaveItem = () => {
    onSave({ ...item, value });
    setIsEditing(false);
  };

  const handleCancelEditItem = () => {
    setValue(item.value);
    setIsEditing(false);
  };

  return (
    <>
      {isEditing ? (
        <InputListItem>
          <StyledRow padding="0px 0px">
            <Input value={value} onChange={(e) => setValue(e.target.value)}></Input>
            <StyledButton onClick={handleCancelEditItem}>Cancel</StyledButton>
            <StyledButton type="primary" onClick={handleSaveItem} icon={<SaveButtonIcon />}>
              Save
            </StyledButton>
          </StyledRow>
        </InputListItem>
      ) : (
        <StyledListItem>
          <StyledRow justifyContent="space-between" style={{ alignItems: 'center' }}>
            <StyledRow justifyContent="flex-start" onClick={() => onClick(item)}>
              {isChoosable && <Checkbox checked={selected} />}
              <div>{item.value}</div>
            </StyledRow>
            <div>
              <StyledEditButton
                type="text"
                size="small"
                onClick={() => setIsEditing(true)}
                icon={<Icon name="Edit" inlineStyle={false} />}
              />
              <StyledDeleteButton
                type="text"
                size="small"
                onClick={() => onDelete(item)}
                icon={<Icon name="Delete" inlineStyle={false} />}
              />
            </div>
          </StyledRow>
        </StyledListItem>
      )}
    </>
  );
};

type DictionaryItemsListProps = {
  currentDictionary: string;
  withItemsSelect: boolean;
  selected: DictionaryType[];
  onClick: (item: DictionaryType) => void;
};

export const DictionaryItemsList: React.FC<DictionaryItemsListProps> = ({
  currentDictionary,
  withItemsSelect,
  selected,
  onClick,
}) => {
  const [newItem, setNewItem] = useState('');

  const { data: dictionary } = useGetDictionaryByName(currentDictionary);

  const saveItem = useSaveDictionaryItem();
  const updateItem = useUpdateDictionaryItem();
  const deleteItem = useDeleteDictionaryItem();

  const handleAddDictionaryItem = () => {
    saveItem.mutate({ queryParams: { dictionary: currentDictionary }, data: { value: newItem } });
    setNewItem('');
  };

  const handleUpdateDictionaryItem = (item: DictionaryType) => {
    updateItem.mutate({ queryParams: { id: item.id, dictionary: currentDictionary }, data: { value: item.value } });
  };

  const handleDeleteDictionaryItem = (item: DictionaryType) => {
    deleteItem.mutate(
      { queryParams: { id: item.id, dictionary: currentDictionary }, data: { value: item.value } },
      {
        onError: (error) => handleFormErrors({ error }),
      }
    );
  };

  if (!dictionary) return <></>;

  return (
    <>
      <ListLabel>Dictionary items</ListLabel>
      <InputListItem>
        <StyledRow padding="0px 0px">
          <Input placeholder="Add item" onChange={(e) => setNewItem(e.target.value)} value={newItem} />
          <StyledButton
            type="primary"
            disabled={!newItem.length}
            onClick={handleAddDictionaryItem}
            icon={<AddButtonIcon />}
          >
            Add
          </StyledButton>
        </StyledRow>
      </InputListItem>

      <DictionaryItemsListContainer>
        {dictionary
          .filter((item) => item.value?.toLowerCase().includes(newItem.toLowerCase()))
          .map((item) => (
            <ListItem
              key={item.id}
              item={item}
              isChoosable={withItemsSelect}
              selected={selected.includes(item)}
              onClick={onClick}
              onDelete={handleDeleteDictionaryItem}
              onSave={handleUpdateDictionaryItem}
            />
          ))}
      </DictionaryItemsListContainer>
    </>
  );
};
